/**
 * The cycle workspace (f10.3), following the prototype's `cycle-workflow.html`:
 * page head, stale banner, the persistent step rail, and an outlet for the
 * overview or the open step.
 *
 * f11.3 (Q-f11.3-2): the page reads `…/input-entry` FIRST. With
 * `workflow_access` it renders the f10.3 workspace -- which reads
 * `GET /api/cycles/{id}` and `…/workflow` -- plus the Forecast input card under
 * Preparation. Without it, it renders `ScopedCycleWorkspace` and never requests
 * the workflow. A `404` renders the standard record-unavailable state.
 */

import { useState } from 'react';
import { Link, Outlet, useOutletContext, useParams } from 'react-router-dom';
import { Download, RefreshCw, Scale } from 'lucide-react';

import type { Cycle, CycleInputEntry, CycleWorkflow, CycleWorkflowStep } from '@/api/client';
import { useCycle, useCycleWorkflow, useInputEntry } from '@/api/queries';
import { RecordUnavailable } from '@/ui/RecordUnavailable';
import { ExportCycleCsvButton } from '@/features/cycle-export/ExportCycleCsvButton';

import { ApplyUpdateDialog } from './ApplyUpdateDialog';
import { CycleStateActions } from './CycleStateActions';
import { CycleStateBadge } from './CycleStateBadge';
import { ForecastInputCard } from './ForecastInputCard';
import { ReviewLink } from './review/ReviewLink';
import { ScopedCycleWorkspace } from './ScopedCycleWorkspace';
import { StepRail } from './StepRail';
import { cycleSubtitle, staleSteps } from './workflowView';

export interface CycleWorkspaceContext {
  cycle: Cycle;
  workflow: CycleWorkflow;
  requestApply: (step: CycleWorkflowStep) => void;
}

/** What the overview and the step page read from the layout. */
export function useCycleWorkspace(): CycleWorkspaceContext {
  return useOutletContext<CycleWorkspaceContext>();
}

function StaleBanner({
  workflow,
  onApply,
}: {
  workflow: CycleWorkflow;
  onApply: (step: CycleWorkflowStep) => void;
}) {
  const stale = staleSteps(workflow);
  if (!stale.length) return null;
  return (
    <div className="cycle-stale-stack">
      {stale.map((step, index) => (
        <div key={step.code} className="alert alert-warning cycle-stale-alert" role="status">
          <RefreshCw aria-hidden="true" />
          <div className="cycle-stale-text">
            <div className="a-title">
              Step {step.number} is stale — {step.stale?.detail ?? 'updates are available'}
            </div>
            {index === 0 && (
              <div className="a-body">
                {step.name} was completed against data that has since changed. Apply the update
                to confirm it, or open the step to re-run it.
              </div>
            )}
          </div>
          {step.capabilities.can_apply_update && (
            <button
              type="button"
              className={index === 0 ? 'btn btn-primary' : 'btn btn-secondary btn-sm'}
              onClick={() => onApply(step)}
            >
              <Download aria-hidden="true" /> Apply update
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

export function CycleWorkspaceLayout() {
  const { cycleId = '' } = useParams();
  const input = useInputEntry(cycleId);

  if (input.error) {
    return (
      <RecordUnavailable
        error={input.error}
        backTo="/cycles"
        backLabel="Cycles"
        onRetry={() => void input.refetch()}
      />
    );
  }
  if (!input.data) return null;
  if (!input.data.workflow_access) return <ScopedCycleWorkspace input={input.data} />;
  return <FinanceCycleWorkspace input={input.data} />;
}

/** The f10.3 workspace, for Finance and Admin, with the f11.3 card in the rail. */
function FinanceCycleWorkspace({ input }: { input: CycleInputEntry }) {
  const { cycleId = '', stepCode } = useParams();
  const cycle = useCycle(cycleId);
  const workflow = useCycleWorkflow(cycleId);
  const [applying, setApplying] = useState<CycleWorkflowStep | null>(null);

  const error = cycle.error ?? workflow.error;
  if (error) {
    return (
      <RecordUnavailable
        error={error}
        backTo="/cycles"
        backLabel="Cycles"
        onRetry={() => {
          void cycle.refetch();
          void workflow.refetch();
        }}
      />
    );
  }
  if (!cycle.data || !workflow.data) return null;

  const context: CycleWorkspaceContext = {
    cycle: cycle.data,
    workflow: workflow.data,
    requestApply: setApplying,
  };
  // Keep the dialog on the freshest copy of the step (its version moves).
  const applyingStep =
    applying && workflow.data.steps.find((step) => step.code === applying.code);

  return (
    <>
      <div className="page-head">
        <div>
          <div className="header-crumb">
            <Link to="/cycles">Cycles</Link> <span>/</span>{' '}
            {stepCode ? <Link to={`/cycles/${cycleId}`}>{cycle.data.code}</Link> : <b>{cycle.data.code}</b>}
          </div>
          <h1 className="page-title">{cycle.data.derived.label}</h1>
          <div className="page-sub">{cycleSubtitle(cycle.data)}</div>
        </div>
        <div className="page-actions">
          <CycleStateBadge state={cycle.data.state} />
          <ReviewLink cycleId={cycleId} />
          {/* f14.2: only this Finance/Admin workspace links to the queue. */}
          <Link className="btn btn-secondary" to={`/cycles/${encodeURIComponent(cycleId)}/reconciliation`}>
            <Scale aria-hidden="true" /> Reconciliation
          </Link>
          <ExportCycleCsvButton cycleId={cycleId} stateCode={cycle.data.state.code} />
          <CycleStateActions cycle={cycle.data} />
        </div>
      </div>

      <StaleBanner workflow={workflow.data} onApply={setApplying} />

      <div className="split-1-2 cycle-workspace">
        <div className="stack">
          <StepRail workflow={workflow.data} cycleId={cycleId} openStepCode={stepCode ?? null} />
          <ForecastInputCard input={input} />
        </div>
        <div className="stack">
          <Outlet context={context} />
        </div>
      </div>

      {applyingStep && (
        <ApplyUpdateDialog
          cycleId={cycleId}
          step={applyingStep}
          onClose={() => setApplying(null)}
        />
      )}
    </>
  );
}
